import type { LucideIcon } from "lucide-react";
import {
  Ban,
  BookOpen,
  Cog,
  Database,
  Headset,
  LayoutDashboard,
  Activity,
  Contact,
  Megaphone,
  PauseCircle,
  Phone,
  PhoneOutgoing,
  PlugZap,
  Route,
  Settings2,
  Star,
  Tag,
  UserCheck,
  UserCog,
  Users,
  Workflow,
  ChartNoAxesCombined,
} from "lucide-react";
import { ANALYTICS_PAGES, ANALYTICS_ROLES, analyticsHref } from "@/features/analytics/analyticsPages";

export type NavItem = {
  title: string;
  to?: string;
  icon?: LucideIcon;
  roles: string[];
  children?: NavItem[];
};

/**
 * Menu lateral em grupos (cada grupo vira um bloco com rótulo; no modo recolhido, só ícones).
 * Para adicionar uma tela: inclua o item no grupo certo com os MESMOS roles da rota em router.tsx.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    title: "Operação",
    roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_SUPER"],
    children: [
      { title: "Painel", to: "/", icon: LayoutDashboard, roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Atendimento", to: "/atendimento", icon: Headset, roles: ["ROLE_USER", "ROLE_ADMIN", "ROLE_SUPER"] },
    ],
  },
  {
    title: "Cadastros",
    roles: ["ROLE_ADMIN", "ROLE_SUPER"],
    children: [
      { title: "Usuários", to: "/users", icon: Users, roles: ["ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Tags", to: "/tags", icon: Tag, roles: ["ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Filas", to: "/queues", icon: Workflow, roles: ["ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Ramais", to: "/extensions", icon: Phone, roles: ["ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Pausas", to: "/breaks", icon: PauseCircle, roles: ["ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Aprovação de pausas", to: "/break-approval", icon: UserCheck, roles: ["ROLE_ADMIN", "ROLE_SUPER"] },
    ],
  },
  {
    title: "Discador",
    icon: PhoneOutgoing,
    roles: ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"],
    children: [
      { title: "Campanhas", to: "/outbound/campaigns", icon: Megaphone, roles: ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Lista de bloqueio (DNC)", to: "/outbound/dnc", icon: Ban, roles: ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Integração Nimo", to: "/outbound/integration", icon: PlugZap, roles: ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Jornada Nimo (demo)", to: "/jornada", icon: Route, roles: ["ROLE_ADMIN", "ROLE_SUPER"] },
    ],
  },
  {
    title: "Relatórios",
    icon: BookOpen,
    roles: ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"],
    children: [
      { title: "Atividade", to: "/records/activity", icon: Activity, roles: ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Agente", to: "/records/agent", icon: UserCog, roles: ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "Cliente", to: "/records/client", icon: Contact, roles: ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"] },
      { title: "NSS", to: "/records/service-score", icon: Star, roles: ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"] },
    ],
  },
  {
    title: "Relatórios gerenciais",
    icon: ChartNoAxesCombined,
    roles: ANALYTICS_ROLES,
    children: ANALYTICS_PAGES.map((p) => ({ title: p.title, to: analyticsHref(p.path), icon: p.icon, roles: ANALYTICS_ROLES })),
  },
  {
    title: "Configuração",
    icon: Cog,
    roles: ["ROLE_SUPER"],
    children: [
      { title: "Parâmetros", to: "/settings/static", icon: Database, roles: ["ROLE_SUPER"] },
      { title: "ServiceNow", to: "/settings/servicenow", icon: Settings2, roles: ["ROLE_SUPER"] },
    ],
  },
];

/** Item do menu correspondente à URL atual (prefixo mais longo) + título do grupo — usado no topo. */
export function findNavMatch(pathname: string): { group?: string; item: NavItem } | null {
  let best: { group?: string; item: NavItem } | null = null;
  let bestLen = -1;
  const visit = (items: NavItem[], group?: string) => {
    for (const it of items) {
      if (it.children) visit(it.children, it.title);
      else if (it.to) {
        const match = it.to === "/" ? pathname === "/" : pathname === it.to || pathname.startsWith(`${it.to}/`);
        if (match && it.to.length > bestLen) {
          best = { group, item: it };
          bestLen = it.to.length;
        }
      }
    }
  };
  visit(NAV_ITEMS);
  return best;
}

export function canAccessNav(item: NavItem, includeAnyRole: (r: string[]) => boolean): boolean {
  if (item.children?.length) {
    return item.children.some((c) => canAccessNav(c, includeAnyRole));
  }
  return includeAnyRole(item.roles);
}
